import { buildOnCommentsClick } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

// The batch list under a matrix-family question, and the block each open batch draws.
//
// A batch is a question in its own right as far as the report params go -- it carries the key and
// id the retired sub-item view derived, and so its own cm/z/s off that key and its cloud off that
// id. The shell reads those params and hands them over; everything else a batch draws is built
// here, off the question the list hangs on.

// the key and id every report param a batch owns hangs off
export const batchKey = (questionKey, position) => `${questionKey}.${position}`
export const batchId = (questionId, position) => `${questionId}-${position}`

// A ranking question names its rows by their position rather than by a reference label.
const RANKING_TYPES = ['ro', 'aro']

// A question with more than one batch shows all of them: hiding one only makes sense while it is
// the only one there is.
export const calcBatches = batches =>
  (batches || [])
    .filter(batch => batch)
    .map(batch => ({ ...batch, hidden: 1 < (batches || []).length ? false : batch.hidden }))

// A batch draws under the question's sub-item settings where it has them, and opens only where
// there is more than one batch to choose between.
export const calcBatchComponents = (components, batches) => ({
  ...(components?.subitems || components),
  expand: 1 < (batches || []).length
})

const batchAnswerOf = (answer, position) => {
  if (null != answer?.[`b${position}`]) return answer[`b${position}`]

  return 'loading' === answer ? 'loading' : { total: 0 }
}

// A batch's answer variants carry its own comment count where they have none of their own, which is
// what the verbatim controls above them read.
const batchAnswerVariants = batch =>
  (batch.answerVariants || []).map(av => {
    if (!av) return null

    av.comments ||= batch.comments

    return av
  })

// a row's comment count opens the comments of the answer it sits on, inside that row
const batchCommentsClick = (questionId, batch) => {
  const open = buildOnCommentsClick({ id: questionId, questionId, batch })
  return (event, position) => open(event, position, batchAnswerVariants(batch))
}

export const buildBatchProps =
  ({
    questionId,
    questionKey,
    type,
    subType,
    format,
    // the chart mode the question declares: a batch draws it when the reader picked none for the
    // batch, and hides its chart when it is `none`
    questionChartMode,
    // the sub-item settings every batch draws under, resolved once for the whole list
    batchComponents,
    answer,
    degree,
    scaleTmpl,
    scaleSteps,
    scalePow,
    defaultSortOrder,
    clickable,
    disabled,
    batchParams = {},
    paramsRevision,
    handlers = {}
  } = {}) =>
  batch => {
    const position = batch?.position
    if (null == position) return null

    const id = batchId(questionId, position)
    const key = batchKey(questionKey, position)

    return {
      id,
      questionKey: key,
      questionType: type,
      questionSubType: subType,
      item: batch,
      answer: batchAnswerOf(answer, position),
      answerVariants: batchAnswerVariants(batch),
      format: { ...(format?.subitems || format), ignoreRefs: RANKING_TYPES.includes(type) },
      questionChartMode,
      components: batchComponents,
      scaleTmpl,
      scaleSteps,
      scalePow,
      degree,
      defaultSortOrder,
      clickable,
      disabled,
      params: batchParams[position] || {},
      paramsRevision,
      handlers: { ...handlers, onCommentsClick: batchCommentsClick(questionId, batch) },
      // the identity travels with the api: the exporters read a batch as a question of its own, and
      // there is no model of its own left to read it off
      onReady: api => handlers.onBatchReady?.(position, api, { id, key, batch }),
      onStateChange: reason => handlers.onBatchStateChange?.(position, key, reason),
      onWcParamsChange: params => handlers.onBatchWcParamsChange?.(id, params)
    }
  }
